import { useState, useCallback, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import { getExtension, getFilename } from '@/lib/utils';

// Ensure argument is an array
const ensureArray = (arr) => (arr && !Array.isArray(arr) ? [arr] : arr);

export function useUploadManager({
  defaultValues: inputDefaultValues,
  values: inputValues,
  onChange,
  multiple = false,
  accept,
  method = 'PUT',
  prepareUpload,
  deleteFile
}) {
  // Make sure the default values are an array
  const defaultValues = useMemo(
    () =>
      ensureArray(inputDefaultValues)?.filter?.(
        (v) => v.url || typeof v === 'string'
      ),
    [inputDefaultValues]
  );

  // IsBusy flags whether an upload or delete is in progress
  const [isBusy, setIsBusy] = useState(false);
  // Values are the URLs to the resulting files
  const [values, setValues] = useState(inputValues || defaultValues || []);

  // Previews is an array of objects that can be rendered
  const [previews, setPreviews] = useState([]);

  // Final values (either uncontrolled or controlled)
  const finalValues = inputValues || values;

  // Handler function to actually upload a collection of file objects
  const uploadFiles = useCallback(
    async (files) => {
      // Make sure another activity isn't in-progress
      if (!isBusy) {
        const finalUploads = [];
        // If we have files to upload
        if (files.length) {
          // Mark as busy
          setIsBusy(true);
          // Set up the previews so they can be represented in the UI immediately
          // If the file is an image-ish extension, create a URL for it, otherwise it's not worth showing
          // The implementation can show an icon based on extension
          // Mime-types are not used because they probably won't be available for default values, but extensions probably will be
          setPreviews(() => {
            if (multiple) {
              return [
                ...Array.from(files).map((file) => {
                  const ext = getExtension(file.name);
                  let url = null;
                  if (
                    ['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)
                  ) {
                    url = URL.createObjectURL(file);
                  }
                  return {
                    id: uuid(),
                    ext,
                    url,
                    filename: file.name,
                    progress: null,
                    preview: true
                  };
                })
              ];
            } else {
              const file = files[0];
              const ext = getExtension(file.name);
              let url = null;
              if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg'].includes(ext)) {
                url = URL.createObjectURL(file);
              }
              return [
                {
                  ext,
                  url,
                  filename: file.name,
                  progress: null,
                  preview: true
                }
              ];
            }
          });
          // Loop through files
          for (let i = 0; i < files.length; i++) {
            // Create a deferred function to account for XHR's callback-based system
            let uploadComplete;
            const uploadPromise = new Promise((resolve) => {
              uploadComplete = resolve;
            });
            // Get the current file
            const file = files[i];
            // Call the "prepareUpload" function-- which should generate a URL to POST to, and a final destination result
            // It can/should also check permissions, and error out if the upload is not allowed.
            const [requestUrl, resultUrl = null] = await prepareUpload(
              file.name
            );
            /* globals XMLHttpRequest */
            // Create the XHR object.
            const xhr = new XMLHttpRequest();
            // Start the progress at 5%
            setPreviews((previews) => {
              previews[i].progress = 5;
              return [...previews];
            });
            // Listen to progress events, and update the tracker as necesary
            xhr.upload.addEventListener('progress', (event) => {
              if (event.lengthComputable) {
                const percent = (event.loaded / event.total) * 80;
                setPreviews((previews) => {
                  previews[i].progress = percent + 5;
                  return [...previews];
                });
              }
            });
            // Listen to "loadend" event, when the upload has finished
            xhr.addEventListener('loadend', async () => {
              const finalValue = resultUrl || requestUrl;
              try {
                // Set the progress to 100%
                // Set the preview URL To the final url and mark it as final
                setPreviews((previews) => {
                  previews[i].progress = 100;
                  previews[i].url = finalValue;
                  previews[i].preview = false;
                  return [...previews];
                });
                // Push the final upload url onto the values array
                finalUploads.push(finalValue);
              } catch (err) {
                /* ignore for now */
              } finally {
                // Complete the deferred promise
                uploadComplete();
              }
            });

            // If an error happen, complete the deferred promise and log the error
            xhr.addEventListener('error', (event) => {
              uploadComplete();
              console.error(event);
            });

            // Start the actual upload using XHR
            xhr.open(method, requestUrl, true);
            xhr.setRequestHeader('Content-Type', 'application/octet-stream');
            xhr.send(file);
            // Await the deferred promise
            await uploadPromise;
          }

          // After a short delay, mark all previews' progress as null (disabling any loading bar)
          setTimeout(() => {
            setPreviews([]);
            // Clear the busy flag
            setIsBusy(false);
            // Set the values to their final form
            // But only if we're using uncontrolled input mode
            if (!inputValues) {
              setValues((values) => [...finalUploads, ...values]);
            }
            // Trigger the field's onChange event, if any.
            if (onChange) {
              onChange([...finalUploads, ...finalValues]);
            }
          }, 200);
        }
      }
    },
    [
      method,
      onChange,
      inputValues,
      finalValues,
      prepareUpload,
      isBusy,
      multiple
    ]
  );

  // Clear out an upload by its index value
  const clearIndex = useCallback(
    async (index) => {
      // Get the value
      const value = finalValues[index];
      if (
        (!inputValues && value !== defaultValues?.[index]) ||
        (inputValues && value !== values[index])
      ) {
        // Only delete from server if we changed the image and haven't saved
        // Server will have to handle deleting old images when we do hit save
        await deleteFile(value);
      }
      // Remove it from the values array
      finalValues.splice(index, 1);
      setValues([...finalValues]);
      return finalValues;
    },
    [finalValues, values, defaultValues, inputValues, deleteFile]
  );

  // Handle clearing one, or all of the uploads
  const clear = useCallback(
    async (index) => {
      // Only proceed if we're not in the middle of a busy event
      if (!isBusy) {
        setIsBusy(true);
        let newValues = [];
        if (index || index === 0) {
          // Clear one
          newValues = await clearIndex(index);
        } else {
          // Try and clear them all.
          await Promise.allSettled(values.map((_v, i) => clearIndex(i)));
        }
        // Clear busy flag.
        setIsBusy(false);
        // Trigger onChange event, if set, either way
        if (onChange) {
          onChange(newValues);
        }
      }
    },
    [values, clearIndex, isBusy, onChange]
  );

  // Trigger a file selection box
  const selectFiles = useCallback(() => {
    // Create the input element
    const fileInput = document.createElement('input');
    fileInput.setAttribute('type', 'file');
    if (accept) {
      fileInput.setAttribute('accept', accept);
    }
    if (multiple) {
      fileInput.setAttribute('multiple', true);
    }
    // When it "changes" (i.e. we select one or more files)
    fileInput.addEventListener('change', async ({ target }) => {
      // If we have files
      if (target && target.files.length) {
        if (multiple) {
          // Upload more if we're allowed
          uploadFiles(target.files);
        } else {
          // No multiples-- clear out the old one first.
          await clear(0);
          uploadFiles([target.files[0]]);
        }
        // Remove the file input from the DOM when we're done
        fileInput.remove();
      }
    });
    // Virtual click!
    fileInput.click();
  }, [multiple, accept, uploadFiles, clear]);

  // Collect the entire upload progress into a single element
  // Only if any one of the previews is in progress.
  let progress = null;
  const inProgress = previews.filter((p) => p.progress);
  if (inProgress.length) {
    // Min 1, max 100, otherwise it's the sum divided my the length of items.
    progress = Math.max(
      1,
      Math.min(
        100,
        Math.round(
          inProgress.reduce((a, p) => a + (p?.progress || 0), 0) /
            inProgress.length
        )
      )
    );
  }

  // Set up "previews" for the default passed-in values as well as the actual previews
  const allPreviews = previews.concat(
    finalValues
      .map((value) => {
        if (value.url) {
          return {
            ...value,
            _id: value.id || value.url,
            ext: getExtension(value.url),
            filename: getFilename(value.url),
            progress: null,
            preview: false
          };
        } else if (typeof value === 'string') {
          return {
            _id: value,
            url: value,
            ext: getExtension(value),
            filename: getFilename(value),
            progress: null,
            preview: false
          };
        } else {
          return null;
        }
      })
      .filter((f) => f)
  );

  // Expose all our fancy file methods
  return {
    selectFiles,
    uploadFiles,
    clear,
    previews: allPreviews,
    progress,
    isBusy,
    values: finalValues
  };
}
